/**
 * Barra de filtros con "Buscar" (QA-2 de la feature 005).
 *
 * `FilterForm` agrupa los controles (`role="search"`) y aplica los filtros con **Enter** dentro de
 * un campo de texto o de fecha, igual que "Buscar". No es un `<form>` a propósito: dentro de un
 * formulario, Radix Select añade un `<select>` nativo oculto por cada selector (duplica sus textos
 * en el DOM) y no hay nada que enviar al servidor. Los selectores, el combobox (que ya consume su
 * propio Enter) y los checkboxes no aplican al cambiar: solo modifican el borrador.
 *
 * `FilterActions` pinta "Buscar" —resaltado en la variante primaria compacta mientras haya cambios
 * sin aplicar, con un aviso para lectores de pantalla— y "Limpiar filtros" cuando hay algo que
 * limpiar. Ambos miden 44 px (objetivo táctil, design-system §10).
 */
import { Search } from 'lucide-react';
import { useId, type HTMLAttributes, type KeyboardEvent, type ReactNode } from 'react';

import { t } from '../../i18n';
import { cn } from '../../lib/cn';

import { Button } from './button';

/** Tipos de `<input>` en los que Enter aplica los filtros. */
const ENTER_APPLIES = new Set(['', 'text', 'search', 'date', 'number', 'email', 'tel']);

export interface FilterFormProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Clases del contenedor; por defecto, la fila flexible de filtros del Admin. */
  className?: string;
  /** Aplica el borrador de filtros (Enter en un campo). */
  onApply: () => void;
  children: ReactNode;
}

export function FilterForm({ onApply, children, className, onKeyDown, ...props }: FilterFormProps) {
  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    onKeyDown?.(event);
    const target = event.target;
    if (
      event.key !== 'Enter' ||
      event.defaultPrevented ||
      !(target instanceof HTMLInputElement) ||
      !ENTER_APPLIES.has(target.getAttribute('type') ?? '') ||
      // Los eventos de React cruzan portales (p. ej. el buscador del combobox): solo cuentan los
      // campos que están de verdad dentro de la barra.
      !event.currentTarget.contains(target)
    ) {
      return;
    }
    event.preventDefault();
    onApply();
  }

  return (
    <div
      role="search"
      onKeyDown={handleKeyDown}
      className={className ?? 'flex flex-wrap items-end gap-fm-3'}
      {...props}
    >
      {children}
    </div>
  );
}

export interface FilterActionsProps {
  /** Aplica el borrador de filtros ("Buscar"). */
  onSearch: () => void;
  /** Hay cambios en el borrador que todavía no se aplicaron. */
  dirty: boolean;
  /** Muestra "Limpiar filtros". */
  canClear?: boolean;
  /** Vuelve a los filtros por defecto y los aplica. */
  onClear?: () => void;
  /** Texto de "Limpiar"; por defecto `common.filters.clear`. */
  clearLabel?: string;
  className?: string;
}

export function FilterActions({
  onSearch,
  dirty,
  canClear = false,
  onClear,
  clearLabel,
  className,
}: FilterActionsProps) {
  const hintId = useId();
  return (
    <div className={cn('flex items-center gap-fm-2', className)}>
      <Button
        variant={dirty ? 'compact' : 'secondary'}
        size="md"
        icon={<Search size={20} />}
        data-testid="filters-search"
        data-dirty={dirty ? 'true' : undefined}
        aria-describedby={dirty ? hintId : undefined}
        onClick={onSearch}
      >
        {t('common.buttons.search')}
      </Button>
      {dirty ? (
        <span id={hintId} className="sr-only">
          {t('common.filters.pending')}
        </span>
      ) : null}
      {canClear && onClear ? (
        <Button variant="ghost" size="md" data-testid="filters-clear" onClick={onClear}>
          {clearLabel ?? t('common.filters.clear')}
        </Button>
      ) : null}
    </div>
  );
}
